import { BaseResponseSchema } from '@monetize/types/common';
import { z } from 'zod';
import { CustomFieldListResSchema } from './customFields.types';
import { OfferingStatusEnumZ } from './enums.types';
import { ProductStatusEnumZ } from './product.types';
import { QuoteRevenueByYearSchema } from './quoteRevenue.types';
import { RateStatusEnumZ } from './rate.types';

/**
 * Overall structure:
 *
 * RuleSchema (Outer rule)
 * -> ruleConditions (Object to store the overall rule conditions)
 * --> rules (Array of conditions (e.x. condition group))
 * ---> conditions (Array of conditions)
 */

export enum RuleTypeEnum {
  APPROVAL = 'APPROVAL',
  VALIDATION = 'VALIDATION',
  CONDITIONAL_TERM = 'CONDITIONAL_TERM',
  RECORD_AUTOMATION = 'RECORD_AUTOMATION',
}

export enum RuleActionTypeEnum {
  APPROVAL = 'APPROVAL',
  VALIDATION = 'VALIDATION',
  CONDITIONAL_TERM = 'CONDITIONAL_TERM',
  RECORD_AUTOMATION = 'RECORD_AUTOMATION',
}

export enum EntityGroupEnum {
  QUOTE = 'QUOTE',
  QUOTE_SHARE = 'QUOTE_SHARE',
  INVOICE = 'INVOICE',
  BILL_GROUP = 'BILL_GROUP',
  CUSTOMER_PORTAL = 'CUSTOMER_PORTAL',
}

export const EntityGroupEnumDisplay: {
  [key in EntityGroupEnum]: string;
} = {
  QUOTE: 'Quote',
  QUOTE_SHARE: 'Quote Share',
  INVOICE: 'Invoice',
  BILL_GROUP: 'Bill Group',
  CUSTOMER_PORTAL: 'Customer Portal',
};

export enum RuleStatusEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export const RuleStatusEnumDisplay: Record<RuleStatusEnum, string> = {
  [RuleStatusEnum.ACTIVE]: 'Active',
  [RuleStatusEnum.INACTIVE]: 'Inactive',
  [RuleStatusEnum.ARCHIVED]: 'Archived',
};

export const RuleStatusEnumDisplayWithoutArchived = {
  [RuleStatusEnum.ACTIVE]: 'Active',
  [RuleStatusEnum.INACTIVE]: 'Inactive',
};

export enum FieldTypeEnum {
  STRING = 'STRING',
  STRING_ARRAY = 'STRING_ARRAY',
  EXISTS = 'EXISTS',
  BOOLEAN = 'BOOLEAN',
  DATE = 'DATE',
  DATE_EXTENDED = 'DATE_EXTENDED',
  NUMBER = 'NUMBER',
  CURRENCY = 'CURRENCY',
  REFERENCE = 'REFERENCE',
  LIST = 'LIST',
  BOOLEAN_LIST = 'BOOLEAN_LIST',
  /**
   * Custom behavior
   * TODO: figure out how to implement this
   */
  // SPECIAL = 'SPECIAL',
}

export enum RecordAutomationUiFieldTypeEnum {
  STRING = 'STRING',
  BOOLEAN = 'BOOLEAN',
  DATE = 'DATE',
  NUMBER = 'NUMBER',
  /** This will allow select one from list of string values */
  LIST = 'LIST',
  /** This type is same as LIST but its value is boolean not string */
  BOOLEAN_LIST = 'BOOLEAN_LIST',
  REFERENCE = 'REFERENCE',
  /** User will input comma separated emails but value is array of emails. Validations/Processing will be done before its sent to Backend. */
  EMAIL_ARRAY = 'EMAIL_ARRAY',
  RICH_TEXT = 'RICH_TEXT',
}

export enum RecordAutomationFieldTypeEnum {
  STRING = 'STRING',
  BOOLEAN = 'BOOLEAN',
  DATE = 'DATE',
  NUMBER = 'NUMBER',
  REFERENCE = 'REFERENCE',
  EMAIL_ARRAY = 'EMAIL_ARRAY',
}

export enum ReferenceEntityEnum {
  ACCOUNT = 'ACCOUNT',
  CURRENCY = 'CURRENCY',
  OFFERING = 'OFFERING',
  PRODUCT = 'PRODUCT',
  RATE = 'RATE',
  RULE = 'RULE',
  TEAM = 'TEAM',
  USER = 'USER',
  DISCOUNTS = 'DISCOUNTS',
  DUNNING_PROCESS = 'DUNNING_PROCESS',
  LEGAL_ENTITY = 'LEGAL_ENTITY',
  QUOTE_DOCUMENT_TYPE = 'QUOTE_DOCUMENT_TYPE',
}

export enum TypeOperatorEnum {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  CURRENCY = 'CURRENCY',
  DATE = 'DATE',
  LIST = 'LIST',
  REFERENCE_LIST = 'REFERENCE_LIST',
  FIELD_VALUES_LIST = 'FIELD_VALUES_LIST',
  /** Composite editor for DATE_OFFSET_FROM_FIELD: magnitude, amount, unit, direction + compare-to field */
  DATE_OFFSET = 'DATE_OFFSET',
}

export enum ExpressionOperatorEnum {
  AND = 'AND',
  OR = 'OR',
  CUSTOM = 'CUSTOM',
}

export const ExpressionOperatorEnumDisplay: {
  [key in ExpressionOperatorEnum]: string;
} = {
  [ExpressionOperatorEnum.AND]: 'All Conditions Are Met',
  [ExpressionOperatorEnum.OR]: 'Any Condition Is Met',
  [ExpressionOperatorEnum.CUSTOM]: 'Custom Logic Is Met',
};

export enum ConditionOperatorEnum {
  EQUAL = 'EQUAL',
  NOT_EQUAL = 'NOT_EQUAL',
  GREATER_THAN = 'GREATER_THAN',
  GREATER_THAN_INCLUSIVE = 'GREATER_THAN_INCLUSIVE',
  LESS_THAN = 'LESS_THAN',
  LESS_THAN_INCLUSIVE = 'LESS_THAN_INCLUSIVE',
  STARTS_WITH = 'STARTS_WITH',
  ENDS_WITH = 'ENDS_WITH',
  CONTAINS = 'CONTAINS',
  NOT_CONTAINS = 'NOT_CONTAINS',
  /** String character-length operators (compare value.length against a number) */
  LENGTH_GREATER_THAN = 'LENGTH_GREATER_THAN',
  LENGTH_LESS_THAN = 'LENGTH_LESS_THAN',
  LENGTH_EQUAL = 'LENGTH_EQUAL',
  IN = 'IN',
  NOT_IN = 'NOT_IN',
  EXISTS = 'EXISTS',
  N_DAYS_IN_PAST = 'N_DAYS_IN_PAST',
  N_WEEKS_IN_PAST = 'N_WEEKS_IN_PAST',
  N_MONTHS_IN_PAST = 'N_MONTHS_IN_PAST',
  N_YEARS_IN_PAST = 'N_YEARS_IN_PAST',
  N_DAYS_IN_FUTURE = 'N_DAYS_IN_FUTURE',
  N_WEEKS_IN_FUTURE = 'N_WEEKS_IN_FUTURE',
  N_MONTHS_IN_FUTURE = 'N_MONTHS_IN_FUTURE',
  N_YEARS_IN_FUTURE = 'N_YEARS_IN_FUTURE',
  FIRST_DAY_OF_MONTH = 'FIRST_DAY_OF_MONTH',
  LAST_DAY_OF_MONTH = 'LAST_DAY_OF_MONTH',
  BEFORE_CURRENT_MONTH = 'BEFORE_CURRENT_MONTH',
  AFTER_CURRENT_MONTH = 'AFTER_CURRENT_MONTH',
  /**
   * Legacy hard-coded comparison against the contract start date. Superseded by
   * DATE_OFFSET_FROM_FIELD, but kept so rules migrated from v2 still evaluate.
   */
  N_DAYS_BEFORE_CONTRACT_START = 'N_DAYS_BEFORE_CONTRACT_START',
  N_DAYS_AFTER_CONTRACT_START = 'N_DAYS_AFTER_CONTRACT_START',
  /**
   * Compares a date field against another date field on the record, offset by N units.
   * The offset lives on `ICondition.dateOffset`; `value` is a FIELD reference to the
   * compare-to date field.
   */
  DATE_OFFSET_FROM_FIELD = 'DATE_OFFSET_FROM_FIELD',
  /**Array type operators */
  INCLUDES = 'INCLUDES',
  NOT_INCLUDES = 'NOT_INCLUDES',
}

export const ConditionOperatorEnumDisplay: {
  [key in ConditionOperatorEnum]: string;
} = {
  [ConditionOperatorEnum.EQUAL]: '=',
  [ConditionOperatorEnum.NOT_EQUAL]: '!=',
  [ConditionOperatorEnum.GREATER_THAN]: '>',
  [ConditionOperatorEnum.GREATER_THAN_INCLUSIVE]: '>=',
  [ConditionOperatorEnum.LESS_THAN]: '<',
  [ConditionOperatorEnum.LESS_THAN_INCLUSIVE]: '<=',
  [ConditionOperatorEnum.STARTS_WITH]: 'Starts With',
  [ConditionOperatorEnum.ENDS_WITH]: 'Ends With',
  [ConditionOperatorEnum.CONTAINS]: 'Contains',
  [ConditionOperatorEnum.NOT_CONTAINS]: 'Does Not Contain',
  [ConditionOperatorEnum.LENGTH_GREATER_THAN]: 'Character Length >',
  [ConditionOperatorEnum.LENGTH_LESS_THAN]: 'Character Length <',
  [ConditionOperatorEnum.LENGTH_EQUAL]: 'Character Length =',
  [ConditionOperatorEnum.IN]: 'In',
  [ConditionOperatorEnum.NOT_IN]: 'Not In',
  [ConditionOperatorEnum.EXISTS]: 'Exists',
  [ConditionOperatorEnum.FIRST_DAY_OF_MONTH]: 'First Day Of Month',
  [ConditionOperatorEnum.LAST_DAY_OF_MONTH]: 'Last Day Of Month',
  [ConditionOperatorEnum.BEFORE_CURRENT_MONTH]: 'Before Current Month',
  [ConditionOperatorEnum.AFTER_CURRENT_MONTH]: 'After Current Month',
  [ConditionOperatorEnum.N_DAYS_IN_PAST]: 'More Than N Days In Past',
  [ConditionOperatorEnum.N_WEEKS_IN_PAST]: 'More Than N Weeks In Past',
  [ConditionOperatorEnum.N_MONTHS_IN_PAST]: 'More Than N Months In Past',
  [ConditionOperatorEnum.N_YEARS_IN_PAST]: 'More Than N Years In Past',
  [ConditionOperatorEnum.N_DAYS_IN_FUTURE]: 'More Than N Days In Future',
  [ConditionOperatorEnum.N_WEEKS_IN_FUTURE]: 'More Than N Weeks In Future',
  [ConditionOperatorEnum.N_MONTHS_IN_FUTURE]: 'More Than N Months In Future',
  [ConditionOperatorEnum.N_YEARS_IN_FUTURE]: 'More Than N Years In Future',
  [ConditionOperatorEnum.N_DAYS_BEFORE_CONTRACT_START]:
    'N Days Before Contract Start Date',
  [ConditionOperatorEnum.N_DAYS_AFTER_CONTRACT_START]:
    'N Days After Contract Start Date',
  [ConditionOperatorEnum.DATE_OFFSET_FROM_FIELD]: 'Differs From Date Field',
  [ConditionOperatorEnum.INCLUDES]: 'Includes',
  [ConditionOperatorEnum.NOT_INCLUDES]: 'Not Includes',
};

export enum AggregateConditionOperatorEnum {
  HAS_SAME_VALUE = 'HAS_SAME_VALUE',
  HAS_DIFFERENT_VALUE = 'HAS_DIFFERENT_VALUE',
  // Count-based operators
  COUNT_EQUALS = 'COUNT_EQUALS',
  COUNT_GREATER_THAN = 'COUNT_GREATER_THAN',
  COUNT_LESS_THAN = 'COUNT_LESS_THAN',
  COUNT_GREATER_THAN_OR_EQUAL = 'COUNT_GREATER_THAN_OR_EQUAL',
  COUNT_LESS_THAN_OR_EQUAL = 'COUNT_LESS_THAN_OR_EQUAL',
  // Sum-based operators
  SUM_EQUALS = 'SUM_EQUALS',
  SUM_GREATER_THAN = 'SUM_GREATER_THAN',
  SUM_LESS_THAN = 'SUM_LESS_THAN',
  SUM_GREATER_THAN_OR_EQUAL = 'SUM_GREATER_THAN_OR_EQUAL',
  SUM_LESS_THAN_OR_EQUAL = 'SUM_LESS_THAN_OR_EQUAL',
}

export const AggregateConditionOperatorEnumDisplay: {
  [key in AggregateConditionOperatorEnum]: string;
} = {
  [AggregateConditionOperatorEnum.HAS_SAME_VALUE]: 'Has Same Value',
  [AggregateConditionOperatorEnum.HAS_DIFFERENT_VALUE]: 'Has Different Value',
  // Count-based operators
  [AggregateConditionOperatorEnum.COUNT_EQUALS]: 'Count Equals',
  [AggregateConditionOperatorEnum.COUNT_GREATER_THAN]: 'Count Greater Than',
  [AggregateConditionOperatorEnum.COUNT_LESS_THAN]: 'Count Less Than',
  [AggregateConditionOperatorEnum.COUNT_GREATER_THAN_OR_EQUAL]:
    'Count Greater Than or Equal To',
  [AggregateConditionOperatorEnum.COUNT_LESS_THAN_OR_EQUAL]:
    'Count Less Than or Equal To',
  // Sum-based operators
  [AggregateConditionOperatorEnum.SUM_EQUALS]: 'Sum Equals',
  [AggregateConditionOperatorEnum.SUM_GREATER_THAN]: 'Sum Greater Than',
  [AggregateConditionOperatorEnum.SUM_LESS_THAN]: 'Sum Less Than',
  [AggregateConditionOperatorEnum.SUM_GREATER_THAN_OR_EQUAL]:
    'Sum Greater Than or Equal To',
  [AggregateConditionOperatorEnum.SUM_LESS_THAN_OR_EQUAL]:
    'Sum Less Than or Equal To',
};

// TODO: figure out what to do here
export enum ConditionOperatorTypeEnum {
  LITERAL = 'LITERAL',
  DATE = 'DATE',
  FIELD = 'FIELD',
}

/**
 * DATE_OFFSET_FROM_FIELD: how far the source date may sit from the compare-to date
 */
export enum DateOffsetMagnitudeEnum {
  MORE_THAN = 'MORE_THAN',
  LESS_THAN = 'LESS_THAN',
}

export const DateOffsetMagnitudeEnumDisplay: {
  [key in DateOffsetMagnitudeEnum]: string;
} = {
  [DateOffsetMagnitudeEnum.MORE_THAN]: 'More Than',
  [DateOffsetMagnitudeEnum.LESS_THAN]: 'Less Than',
};

export enum DateOffsetUnitEnum {
  DAYS = 'DAYS',
  WEEKS = 'WEEKS',
  MONTHS = 'MONTHS',
  YEARS = 'YEARS',
}

export const DateOffsetUnitEnumDisplay: {
  [key in DateOffsetUnitEnum]: string;
} = {
  [DateOffsetUnitEnum.DAYS]: 'Days',
  [DateOffsetUnitEnum.WEEKS]: 'Weeks',
  [DateOffsetUnitEnum.MONTHS]: 'Months',
  [DateOffsetUnitEnum.YEARS]: 'Years',
};

export enum DateOffsetDirectionEnum {
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
  EITHER = 'EITHER',
}

export const DateOffsetDirectionEnumDisplay: {
  [key in DateOffsetDirectionEnum]: string;
} = {
  [DateOffsetDirectionEnum.BEFORE]: 'Before',
  [DateOffsetDirectionEnum.AFTER]: 'After',
  [DateOffsetDirectionEnum.EITHER]: 'Either Direction',
};

/**
 * Entities available for Bill Group based rules
 */
export enum BillGroupEntityEnum {
  BILL_GROUP = 'BILL_GROUP',
  QUOTE = 'QUOTE',
  QUOTE_CONDITIONAL_TERMS_FIELDS = 'QUOTE_CONDITIONAL_TERMS_FIELDS',
  QUOTE_OFFERING = 'QUOTE_OFFERING',
  QUOTE_ITEM = 'QUOTE_ITEM',
}

export const BillGroupEntityEnumDisplay: {
  [key in BillGroupEntityEnum]: string;
} = {
  [BillGroupEntityEnum.BILL_GROUP]: 'Bill Group',
  [BillGroupEntityEnum.QUOTE]: 'Quote',
  [BillGroupEntityEnum.QUOTE_CONDITIONAL_TERMS_FIELDS]:
    'Quote Conditional Terms',
  [BillGroupEntityEnum.QUOTE_OFFERING]: 'Quote Offering',
  [BillGroupEntityEnum.QUOTE_ITEM]: 'Quote Item',
};

/**
 * Entities available for Quote based rules
 */
export enum QuoteEntityEnum {
  QUOTE = 'QUOTE',
  QUOTE_CONDITIONAL_TERMS_FIELDS = 'QUOTE_CONDITIONAL_TERMS_FIELDS',
  QUOTE_OFFERING = 'QUOTE_OFFERING',
  QUOTE_ITEM = 'QUOTE_ITEM',
  QUOTE_DOCUMENT_FIELDS = 'QUOTE_DOCUMENT_FIELDS',
}

export const QuoteEntityEnumDisplay: { [key in QuoteEntityEnum]: string } = {
  [QuoteEntityEnum.QUOTE]: 'Quote',
  [QuoteEntityEnum.QUOTE_CONDITIONAL_TERMS_FIELDS]: 'Quote Conditional Terms',
  [QuoteEntityEnum.QUOTE_OFFERING]: 'Quote Offering',
  [QuoteEntityEnum.QUOTE_ITEM]: 'Quote Item',
  [QuoteEntityEnum.QUOTE_DOCUMENT_FIELDS]: 'Quote Documents',
};

/**
 * Entities available for Invoice based rules
 */
export enum InvoiceEntityEnum {
  INVOICE = 'INVOICE',
  INVOICE_ITEM = 'INVOICE_ITEM',
}

export const InvoiceEntityEnumDisplay: { [key in InvoiceEntityEnum]: string } =
  {
    [InvoiceEntityEnum.INVOICE]: 'Invoice',
    [InvoiceEntityEnum.INVOICE_ITEM]: 'Invoice Item',
  };

/**
 * Entities available for Customer Portal based rules. Customer Portal rules
 * evaluate against a Bill Group payload, so conditions are restricted to the
 * Bill Group entity (no Quote-related entities).
 */
export enum CustomerPortalEntityEnum {
  BILL_GROUP = 'BILL_GROUP',
}

export const CustomerPortalEntityEnumDisplay: {
  [key in CustomerPortalEntityEnum]: string;
} = {
  [CustomerPortalEntityEnum.BILL_GROUP]: 'Bill Group',
};

export type RuleConditionEntityEnum =
  | QuoteEntityEnum
  | BillGroupEntityEnum
  | InvoiceEntityEnum
  | CustomerPortalEntityEnum;
export const RuleConditionEntityUnion = z.union([
  z.enum(QuoteEntityEnum),
  z.enum(BillGroupEntityEnum),
  z.enum(InvoiceEntityEnum),
  z.enum(CustomerPortalEntityEnum),
]);

export const RuleEntityEnumDisplay = {
  [EntityGroupEnum.QUOTE]: QuoteEntityEnumDisplay,
  [EntityGroupEnum.QUOTE_SHARE]: QuoteEntityEnumDisplay,
  [EntityGroupEnum.BILL_GROUP]: BillGroupEntityEnumDisplay,
  [EntityGroupEnum.INVOICE]: InvoiceEntityEnumDisplay,
  [EntityGroupEnum.CUSTOMER_PORTAL]: CustomerPortalEntityEnumDisplay,
};

export enum RuleValidationErrorLevelEnum {
  INFO = 'INFO',
  ERROR = 'ERROR',
  WARN = 'WARN',
}

export const RuleValidationErrorLevelEnumZ = z.enum(
  RuleValidationErrorLevelEnum,
);

export const RuleValidationErrorLevelDisplay: {
  [key in RuleValidationErrorLevelEnum]: string;
} = {
  INFO: 'Info',
  ERROR: 'Error',
  WARN: 'Warning',
};

export enum RuleValidationErrorLocationEnum {
  QUOTE_HEADER = 'QUOTE_HEADER',
  SPECIFIC_FIELDS = 'SPECIFIC_FIELDS',
}

export const RuleValidationErrorLocationEnumZ = z.enum(
  RuleValidationErrorLocationEnum,
);

export const RuleValidationErrorLocationDisplay: {
  [key in RuleValidationErrorLocationEnum]: string;
} = {
  QUOTE_HEADER: 'Quote header',
  SPECIFIC_FIELDS: 'Specific field(s)',
};

export const RuleValidationErrorLocationHint: {
  [key in RuleValidationErrorLocationEnum]: string;
} = {
  QUOTE_HEADER:
    'This message will display in the Quote Alerts panel on the Quote Header, and is not tied to a specific field.',
  SPECIFIC_FIELDS:
    'This message will display inline under the selected field(s) and the Quote Alerts panel will link to the selected field(s) for the user to review.',
};

/**
 * `label` is snapshotted at rule-authoring time on purpose: the quote page
 * renders "Jump to <label>" but has no access to the rules schema that produced
 * it, so the key alone is not resolvable there. Those labels are namespaced for
 * the builder's dropdown (`Contacts -> Billing Email`) — display them through
 * `getValidationFieldLabel`, never raw.
 */
export const RuleValidationFieldSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
});
export type RuleValidationField = z.infer<typeof RuleValidationFieldSchema>;

export const RuleTypeEnumZ = z.enum(RuleTypeEnum);
export const RuleActionTypeEnumZ = z.enum(RuleActionTypeEnum);
export const EntityGroupEnumZ = z.enum(EntityGroupEnum);
export const RuleStatusEnumZ = z.enum(RuleStatusEnum);
export const FieldTypeEnumZ = z.enum(FieldTypeEnum);
export const RecordAutomationUiFieldTypeEnumZ = z.enum(
  RecordAutomationUiFieldTypeEnum,
);
export const RecordAutomationFieldTypeEnumZ = z.enum(
  RecordAutomationFieldTypeEnum,
);
export const ReferenceEntityEnumZ = z.enum(ReferenceEntityEnum);
export const TypeOperatorEnumZ = z.enum(TypeOperatorEnum);
export const ExpressionOperatorEnumZ = z.enum(ExpressionOperatorEnum);
export const ConditionOperatorEnumZ = z.enum(ConditionOperatorEnum);
export const AggregateConditionOperatorEnumZ = z.enum(
  AggregateConditionOperatorEnum,
);
export const ConditionOperatorTypeEnumZ = z.enum(ConditionOperatorTypeEnum);
export const DateOffsetMagnitudeEnumZ = z.enum(DateOffsetMagnitudeEnum);
export const DateOffsetUnitEnumZ = z.enum(DateOffsetUnitEnum);
export const DateOffsetDirectionEnumZ = z.enum(DateOffsetDirectionEnum);

export const BillGroupEntityEnumZ = z.enum(BillGroupEntityEnum);
export const QuoteEntityEnumZ = z.enum(QuoteEntityEnum);
export const InvoiceEntityEnumZ = z.enum(InvoiceEntityEnum);
export const CustomerPortalEntityEnumZ = z.enum(CustomerPortalEntityEnum);

/**
 * TYPES
 */

export const RuleFormFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: FieldTypeEnumZ,
  values: z
    .object({
      label: z.string(),
      value: z.union([z.string(), z.boolean()]),
    })
    .array()
    .nullish(),
  referenceEntity: ReferenceEntityEnumZ.nullish(),
  operatorOverride: z
    .object({
      label: z.string(),
      value: ConditionOperatorEnumZ,
    })
    .array()
    .nullish(),
  // This is parent object's field, not direct entity's field
  isLookupField: z.boolean().nullish(),
});

export const RuleFormFieldMapSchema = z.record(z.string(), RuleFormFieldSchema);

export const RuleFormBillGroupFieldEntitySchema = z.object({
  entity: BillGroupEntityEnumZ,
  fields: RuleFormFieldMapSchema,
  keyPrefix: z.string().nullish(),
});

export const RuleFormQuoteFieldEntitySchema = z.object({
  entity: QuoteEntityEnumZ,
  fields: RuleFormFieldMapSchema,
});

export const RuleFormQuoteShareFieldEntitySchema = z.object({
  entity: QuoteEntityEnumZ,
  fields: RuleFormFieldMapSchema,
  keyPrefix: z.string().nullish(),
});

export const RuleFormInvoiceFieldEntitySchema = z.object({
  entity: InvoiceEntityEnumZ,
  fields: RuleFormFieldMapSchema,
});

export const RuleFormCustomerPortalFieldEntitySchema = z.object({
  entity: CustomerPortalEntityEnumZ,
  fields: RuleFormFieldMapSchema,
  keyPrefix: z.string().nullish(),
});

export const TypeOperatorSchema = z.object({
  type: TypeOperatorEnumZ,
  options: z
    .object({
      label: z.string(),
      value: z.union([z.string(), z.boolean()]),
    })
    .array()
    .nullish(),
});

export const TypeOperatorMapSchema = z.object({
  operators: z.record(ConditionOperatorEnumZ, TypeOperatorSchema),
});

export const TypeOperatorByFieldTypeSchema = z.record(
  FieldTypeEnumZ,
  TypeOperatorMapSchema,
);

export const AggregateConditionSchema = z.object({
  field: z.string().nullish(),
  operator: AggregateConditionOperatorEnumZ,
  value: z.union([z.literal('TRUE'), z.literal('FALSE'), z.number()]),
  /**
   * If provided, records will grouped by this field and each group is evaluated individually
   */
  scopedToGroupField: z.string().nullish(),
});

export const ConditionValueSchema = z.object({
  tempType: ConditionOperatorTypeEnumZ.nullish(),
  type: ConditionOperatorTypeEnumZ, // FIXME: not sure what values to use here to describe the types, should have discriminated union
  // FIXME: we will need to support more types here, such as lists and potentially references?
  value: z.union([
    z.string().min(1, {
      error: 'Value is required',
    }),
    z.number(),
    z.boolean(),
    z.array(z.string()),
  ]),
});

/**
 * Offset applied to the compare-to date for DATE_OFFSET_FROM_FIELD, e.g.
 * "more than 5 days before". Zero is excluded: it is vacuous for LESS_THAN and
 * already expressible with `!=` / `<` / `>` for MORE_THAN.
 */
export const DateOffsetSchema = z.object({
  magnitude: DateOffsetMagnitudeEnumZ,
  amount: z
    .number({ error: 'Amount is required' })
    .int({ error: 'Amount must be a whole number' })
    .min(1, { error: 'Amount must be at least 1' }),
  unit: DateOffsetUnitEnumZ,
  direction: DateOffsetDirectionEnumZ,
});

export const ConditionSchema = z
  .object({
    field: z.string().min(1, {
      error: 'Field is required',
    }),
    operator: ConditionOperatorEnumZ,
    value: ConditionValueSchema,
    /**
     * Required when operator is DATE_OFFSET_FROM_FIELD; `value` then holds a FIELD
     * reference to the compare-to date field. Ignored for every other operator.
     */
    dateOffset: DateOffsetSchema.nullish(),
  })
  .superRefine((condition, ctx) => {
    if (condition.operator !== ConditionOperatorEnum.DATE_OFFSET_FROM_FIELD) {
      return;
    }
    if (!condition.dateOffset) {
      ctx.addIssue({
        code: 'custom',
        message: 'Date offset is required',
        path: ['dateOffset'],
      });
    }
    if (condition.value.type !== ConditionOperatorTypeEnum.FIELD) {
      ctx.addIssue({
        code: 'custom',
        message: 'A date field to compare against is required',
        path: ['value', 'type'],
      });
    }
  });

export const ALLOW_AGGREGATE_ENTITIES = new Set<RuleConditionEntityEnum>([
  QuoteEntityEnum.QUOTE_ITEM,
  QuoteEntityEnum.QUOTE_OFFERING,
]);

export const ALLOW_DELETED_RECORDS = new Set<RuleConditionEntityEnum>([
  QuoteEntityEnum.QUOTE_ITEM,
  QuoteEntityEnum.QUOTE_OFFERING,
]);

export const RuleConditionSchema = z
  .object({
    entity: RuleConditionEntityUnion,
    expressionOperator: ExpressionOperatorEnumZ,
    /**
     * Applies if expressionOperator is CUSTOM and allows users to build complex conditions
     */
    customLogic: z.string().nullish(),
    conditions: z.array(ConditionSchema).min(1),
    includeDeletedRecords: z.boolean().default(false).nullish(),
    /**
     * If true, the rule outcome will be reversed
     */
    negate: z.boolean().default(false).nullish(),
    /**
     * Rule to apply to all records that evaluated to true based on conditions
     */
    aggregateCondition: AggregateConditionSchema.nullish(),
  })
  .refine(
    (value) =>
      !value.aggregateCondition || ALLOW_AGGREGATE_ENTITIES.has(value.entity),
    {
      error: `This does not support aggregate conditions.`,
    },
  )
  .refine(
    (value) =>
      value.expressionOperator !== ExpressionOperatorEnum.CUSTOM ||
      (!!value.customLogic && value.customLogic.length >= 1),
    { message: `Custom logic is required.` },
  );

export const RuleActionSchema = z.object({
  actionType: RuleTypeEnumZ,
});

export const ApprovalItemSchema = z.object({
  userId: z.string().nullish(),
  username: z.string().nullish(),
  teamId: z.string().nullish(),
  teamName: z.string().nullish(),
  mandatory: z.boolean().default(true).nullish(),
  order: z.number().default(1).nullish(),
});

export type ApprovalItem = z.infer<typeof ApprovalItemSchema>;

export const RuleActionApprovalSchema = z.object({
  actionType: z.literal(RuleTypeEnum.APPROVAL),
  approval: z
    .object({
      userId: z.string().nullish(),
      username: z.string().nullish(),
      teamId: z.string().nullish(),
      teamName: z.string().nullish(),
      mandatory: z.boolean(),
      priority: z.number(),
    })
    .optional(),
  approvals: z.array(ApprovalItemSchema).min(1).optional(),
});

// Form-only: every approver row needs a user or a team. Kept off
// RuleActionApprovalSchema so rules already saved without one still load.
const RuleActionApprovalFormSchema = RuleActionApprovalSchema.extend({
  approvals: z
    .array(
      ApprovalItemSchema.refine(({ userId, teamId }) => !!userId || !!teamId, {
        message: 'Approver is required',
        path: ['userId'],
      }),
    )
    .min(1)
    .optional(),
});

export const RuleActionConditionalTermSchema = z.object({
  actionType: z.literal(RuleTypeEnum.CONDITIONAL_TERM),
  conditionalTerms: z.object({
    displayOrder: z.number(),
    editableOnQuote: z.boolean(),
    terms: z.string(),
  }),
});

export const RuleActionValidationSchema = z.object({
  actionType: z.literal(RuleTypeEnum.VALIDATION),
  validation: z
    .object({
      errorLevel: RuleValidationErrorLevelEnumZ,
      message: z.string().min(1, {
        error: 'Message is required',
      }),
      errorLocation: RuleValidationErrorLocationEnumZ.nullish(),
      fields: RuleValidationFieldSchema.array().nullish(),
    })
    .refine(
      (validation) =>
        validation.errorLocation !==
          RuleValidationErrorLocationEnum.SPECIFIC_FIELDS ||
        (validation.fields?.length ?? 0) > 0,
      {
        error: 'At least one field is required',
        path: ['fields'],
      },
    ),
});

export enum RuleFieldTypeEnum {
  ALL_DAYS = 'ALL_DAYS',
  EVERY_DAY = 'EVERY_DAY',
}

export enum RecordUpdateValueTypeEnum {
  CONSTANT = 'CONSTANT',
  FIELD = 'FIELD',
}
export const RecordUpdateValueTypeEnumZ = z.enum([
  RecordUpdateValueTypeEnum.CONSTANT,
  RecordUpdateValueTypeEnum.FIELD,
]);
export const RecordUpdateValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.array(z.string()),
]);
export type RecordUpdateValue = z.infer<typeof RecordUpdateValueSchema>;

export const RecordUpdateValueTypeEnumDisplay: {
  [key in RecordUpdateValueTypeEnum]: string;
} = {
  CONSTANT: 'Constant',
  FIELD: 'Field',
};

export const RuleActionRecordAutomationValueSchema = z
  .object({
    targetField: z.string().min(1, {
      error: 'Field is required',
    }),
    value: RecordUpdateValueSchema,
    valueType: RecordUpdateValueTypeEnumZ,
    valueField: z.string().nullish(),
    type: RecordAutomationFieldTypeEnumZ,
  })
  .refine(
    (value) =>
      value.valueType === RecordUpdateValueTypeEnum.CONSTANT ||
      (value.valueType === RecordUpdateValueTypeEnum.FIELD && value.valueField),
    { message: `This is required when value type is Field` },
  )
  .refine(
    ({ value, type }) => {
      if (type !== RecordAutomationFieldTypeEnum.EMAIL_ARRAY) {
        return true;
      }

      const isAllEmailValid =
        Array.isArray(value) &&
        value.every((email: string) => z.email().safeParse(email).success);

      return isAllEmailValid;
    },
    {
      error: 'Contains invalid email address',
    },
  );

export type RuleActionRecordAutomationValue = z.infer<
  typeof RuleActionRecordAutomationValueSchema
>;

export const RuleActionRecordAutomationSchema = z.object({
  actionType: z.literal(RuleTypeEnum.RECORD_AUTOMATION),
  values: RuleActionRecordAutomationValueSchema.array(),
  priority: z.number().min(1),
});

export enum RuleTimePeriodEvaluationEnum {
  ALL_DAYS = 'ALL_DAYS',
  EVERY_DAY = 'EVERY_DAY',
}

export const RuleTimePeriodEvaluationEnumZ = z.enum([
  RuleTimePeriodEvaluationEnum.ALL_DAYS,
  RuleTimePeriodEvaluationEnum.EVERY_DAY,
]);

export const RuleConditionsSchema = z
  .object({
    expressionOperator: ExpressionOperatorEnumZ,
    customLogic: z.string().nullish(),
    rules: z.array(RuleConditionSchema).min(1),
    ruleTimePeriodEvaluation: RuleTimePeriodEvaluationEnumZ.default(
      RuleTimePeriodEvaluationEnum.ALL_DAYS,
    ),
  })
  .refine(
    (value) =>
      value.expressionOperator !== ExpressionOperatorEnum.CUSTOM ||
      (!!value.customLogic && value.customLogic.length >= 1),
    { message: `Custom logic is required.` },
  );

// Form schema without refine() for react-hook-form compatibility
// Uses superRefine() instead to keep it as a ZodObject
export const RuleConditionsFormSchema = z
  .object({
    expressionOperator: ExpressionOperatorEnumZ,
    customLogic: z.string().nullish(),
    rules: z.array(RuleConditionSchema).min(1),
    ruleTimePeriodEvaluation: RuleTimePeriodEvaluationEnumZ, // Required, provided via defaultValues
  })
  .superRefine((value, ctx) => {
    if (
      value.expressionOperator === ExpressionOperatorEnum.CUSTOM &&
      (!value.customLogic || value.customLogic.length < 1)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Custom logic is required.',
      });
    }
  });

export const RuleSchema = BaseResponseSchema.extend({
  id: z.string().nullish(),
  name: z
    .string()
    .min(1, {
      error: 'Name is required',
    })
    .max(255),
  description: z.string().max(500).nullish(),
  type: RuleTypeEnumZ,
  target: EntityGroupEnumZ,
  status: RuleStatusEnumZ,
  ruleConditions: RuleConditionsSchema,
  action: z.discriminatedUnion('actionType', [
    RuleActionApprovalSchema,
    RuleActionConditionalTermSchema,
    RuleActionValidationSchema,
    RuleActionRecordAutomationSchema,
  ]),
  sortOrder: z.number().nullish(),
  createDate: z.union([z.string(), z.date()]).nullish(),
  createdBy: z.union([z.string(), z.date()]).nullish(),
  modifyDate: z.union([z.string(), z.date()]).nullish(),
  lastModifiedBy: z.union([z.string(), z.date()]).nullish(),
});

export const RuleResponseSchema = RuleSchema.extend({
  id: z.string(),
  createDate: z.union([z.string(), z.date()]),
  createdBy: z.union([z.string(), z.date()]),
  modifyDate: z.union([z.string(), z.date()]),
  lastModifiedBy: z.union([z.string(), z.date()]),
});

/**
 * This is here because adding `.refine` to a type changes it from a ZodObject
 * to a ZodEffect, which means that a bunch of behavior is lost
 *
 * see @link https://github.com/colinhacks/zod/issues/2474
 *
 */
export const RuleSchema_Refined = RuleSchema.refine(
  (rule) => {
    if (rule.type === RuleTypeEnum.APPROVAL) {
      return rule.action.actionType === RuleTypeEnum.APPROVAL;
    }
    if (rule.type === RuleTypeEnum.CONDITIONAL_TERM) {
      return rule.action.actionType === RuleTypeEnum.CONDITIONAL_TERM;
    }
    if (rule.type === RuleTypeEnum.VALIDATION) {
      return rule.action.actionType === RuleTypeEnum.VALIDATION;
    }
    if (rule.type === RuleTypeEnum.RECORD_AUTOMATION) {
      return rule.action.actionType === RuleTypeEnum.RECORD_AUTOMATION;
    }
    return true;
  },
  { error: `Rule's type does not match the action's Type` },
);

export const RecordAutomationActionFieldsSchema = z.object({
  label: z.string(),
  key: z.string(),
  type: RecordAutomationUiFieldTypeEnumZ,
  referenceEntity: ReferenceEntityEnumZ.nullish(),
  allowNull: z.boolean(),
  values: z
    .object({
      label: z.string(),
      value: z.union([z.string(), z.boolean()]),
      warning: z.string().nullish(),
    })
    .array()
    .optional(),
  hint: z.string().nullish(),
  allowedValueTypes: RecordUpdateValueTypeEnumZ.array().optional(),
});
export type RecordAutomationActionFields = z.infer<
  typeof RecordAutomationActionFieldsSchema
>;

export const RuleFormResponseBaseSchema = z.object({
  operators: ConditionOperatorEnumZ.array(),
  operatorTypes: TypeOperatorByFieldTypeSchema,
  aggregateOperators: AggregateConditionOperatorEnumZ.array(),
  recordAutomationActionFields:
    RecordAutomationActionFieldsSchema.array().optional(),
});
export const RuleFormBillGroupResponseSchema =
  RuleFormResponseBaseSchema.extend({
    entity: z.literal(EntityGroupEnum.BILL_GROUP),
    fields: z.record(BillGroupEntityEnumZ, RuleFormBillGroupFieldEntitySchema),
    keyPrefix: z.string().nullish(),
  });
export const RuleFormQuoteResponseSchema = RuleFormResponseBaseSchema.extend({
  entity: z.literal(EntityGroupEnum.QUOTE),
  fields: z.record(QuoteEntityEnumZ, RuleFormQuoteFieldEntitySchema),
  keyPrefix: z.string().nullish(),
});
export const RuleFormQuoteShareResponseSchema =
  RuleFormResponseBaseSchema.extend({
    entity: z.literal(EntityGroupEnum.QUOTE_SHARE),
    fields: z.record(QuoteEntityEnumZ, RuleFormQuoteShareFieldEntitySchema),
    keyPrefix: z.string().nullish(),
  });
export const RuleFormInvoiceResponseSchema = RuleFormResponseBaseSchema.extend({
  entity: z.literal(EntityGroupEnum.INVOICE),
  fields: z.record(InvoiceEntityEnumZ, RuleFormInvoiceFieldEntitySchema),
  keyPrefix: z.string().nullish(),
});
export const RuleFormCustomerPortalResponseSchema =
  RuleFormResponseBaseSchema.extend({
    entity: z.literal(EntityGroupEnum.CUSTOMER_PORTAL),
    fields: z.record(
      CustomerPortalEntityEnumZ,
      RuleFormCustomerPortalFieldEntitySchema,
    ),
    keyPrefix: z.string().nullish(),
  });

// zod record has all keys as optional, force them to be required and combine operatorTypes
// we are not using a discriminated union here because we need to combine the operatorTypes and we cannot extend union types
export interface RuleFormBillGroupResponse extends z.infer<
  typeof RuleFormBillGroupResponseSchema
> {
  fields: Required<z.infer<typeof RuleFormBillGroupResponseSchema>['fields']>;
  operatorTypes: Record<
    FieldTypeEnum,
    Required<z.infer<typeof TypeOperatorMapSchema>>
  >;
}
export interface RuleFormQuoteResponse extends z.infer<
  typeof RuleFormQuoteResponseSchema
> {
  fields: Required<z.infer<typeof RuleFormQuoteResponseSchema>['fields']>;
  operatorTypes: Record<
    FieldTypeEnum,
    Required<z.infer<typeof TypeOperatorMapSchema>>
  >;
}
export interface RuleFormQuoteShareResponse extends z.infer<
  typeof RuleFormQuoteShareResponseSchema
> {
  // Uses quote fields
  fields: Required<z.infer<typeof RuleFormQuoteShareResponseSchema>['fields']>;
  operatorTypes: Record<
    FieldTypeEnum,
    Required<z.infer<typeof TypeOperatorMapSchema>>
  >;
}
export interface RuleFormInvoiceResponse extends z.infer<
  typeof RuleFormInvoiceResponseSchema
> {
  fields: Required<z.infer<typeof RuleFormInvoiceResponseSchema>['fields']>;
  operatorTypes: Record<
    FieldTypeEnum,
    Required<z.infer<typeof TypeOperatorMapSchema>>
  >;
}
export interface RuleFormCustomerPortalResponse extends z.infer<
  typeof RuleFormCustomerPortalResponseSchema
> {
  // Reuses Bill Group condition fields
  fields: Required<
    z.infer<typeof RuleFormCustomerPortalResponseSchema>['fields']
  >;
  operatorTypes: Record<
    FieldTypeEnum,
    Required<z.infer<typeof TypeOperatorMapSchema>>
  >;
}

export const RuleTestEvaluationResponseSchema = z.object({
  conditionResults: z.boolean().array(),
  finalResult: z.boolean(),
});

export type RuleFormFieldMap = z.infer<typeof RuleFormFieldMapSchema>;
export type RuleFormBillGroupFieldEntity = z.infer<
  typeof RuleFormBillGroupFieldEntitySchema
>;
export type RuleFormQuoteFieldEntity = z.infer<
  typeof RuleFormQuoteFieldEntitySchema
>;
export type RuleFormInvoiceFieldEntity = z.infer<
  typeof RuleFormInvoiceFieldEntitySchema
>;
export type RuleFormCustomerPortalFieldEntity = z.infer<
  typeof RuleFormCustomerPortalFieldEntitySchema
>;
export type RuleFormFieldEntity =
  | RuleFormBillGroupFieldEntity
  | RuleFormQuoteFieldEntity
  | RuleFormInvoiceFieldEntity
  | RuleFormCustomerPortalFieldEntity;
export type RuleFormField = z.infer<typeof RuleFormFieldSchema>;
export type TypeOperator = z.infer<typeof TypeOperatorSchema>;
export type TypeOperatorMap = z.infer<typeof TypeOperatorMapSchema>;
// Zod uses Partial for records, but all fields will be present and are required
export type TypeOperatorByFieldType = Record<
  FieldTypeEnum,
  Required<z.infer<typeof TypeOperatorMapSchema>>
>;
export type IAggregateCondition = z.infer<typeof AggregateConditionSchema>;
export type ConditionValue = z.infer<typeof ConditionValueSchema>;
export type DateOffset = z.infer<typeof DateOffsetSchema>;
export type ICondition = z.infer<typeof ConditionSchema>;
export type IRuleCondition = z.infer<typeof RuleConditionSchema>;
export type IRuleConditions = z.infer<typeof RuleConditionsSchema>;
export type RuleActionApproval = z.infer<typeof RuleActionApprovalSchema>;
export type RuleActionConditionalTerm = z.infer<
  typeof RuleActionConditionalTermSchema
>;
export type RuleActionValidation = z.infer<typeof RuleActionValidationSchema>;
export type RuleActionRecordAutomation = z.infer<
  typeof RuleActionRecordAutomationSchema
>;
export type RuleAction = z.infer<typeof RuleSchema>['action'];
export type IRule = z.infer<typeof RuleSchema>;
export type RuleResponse = z.infer<typeof RuleResponseSchema>;

// Form schema without refine() for react-hook-form compatibility
export const RuleFormSchema = BaseResponseSchema.extend({
  id: z.string().nullish(),
  name: z
    .string()
    .min(1, {
      error: 'Name is required',
    })
    .max(255),
  description: z.string().max(500).nullish(),
  type: RuleTypeEnumZ,
  target: EntityGroupEnumZ,
  status: RuleStatusEnumZ,
  ruleConditions: RuleConditionsFormSchema,
  action: z.discriminatedUnion('actionType', [
    RuleActionApprovalFormSchema,
    RuleActionConditionalTermSchema,
    RuleActionValidationSchema,
    RuleActionRecordAutomationSchema,
  ]),
  sortOrder: z.number().nullish(),
  createDate: z.union([z.string(), z.date()]).nullish(),
  createdBy: z.union([z.string(), z.date()]).nullish(),
  modifyDate: z.union([z.string(), z.date()]).nullish(),
  lastModifiedBy: z.union([z.string(), z.date()]).nullish(),
});

export type IRuleForm = z.infer<typeof RuleFormSchema>;

export type IRuleFormResponse =
  | RuleFormBillGroupResponse
  | RuleFormQuoteResponse
  | RuleFormQuoteShareResponse
  | RuleFormInvoiceResponse
  | RuleFormCustomerPortalResponse;

export type RuleTestEvaluationResponse = z.infer<
  typeof RuleTestEvaluationResponseSchema
>;

export interface IRuleFormResponseWithEntities {
  ruleFormData: IRuleFormResponse;
  entities: RuleConditionEntityEnum[];
}

/**
 * REQUEST / RESPONSE SCHEMAS
 */

export const ApprovalRuleResponseSchema = z.object({
  userId: z.string().nullish(),
  username: z.string().nullish(),
  teamId: z.string().nullish(),
  teamName: z.string().nullish(),
  rank: z.number(),
  /** NOTE: this is the inverse of skippable, used for quote approval dto */
  mandatory: z.boolean(),
  /** Does not appear to be on the response, just the request */
  skipped: z.boolean().nullish(),
});

export const MultiApprovalsRuleResponseSchema = z.object({
  type: z.enum(['USER', 'TEAM']),
  userId: z.string().nullish(),
  username: z.string().nullish(),
  teamId: z.string().nullish(),
  teamName: z.string().nullish(),
  order: z.number(),
});

const ValidationRuleResponseSchema = z.object({
  errorLevel: z.enum(['INFO', 'WARN', 'ERROR']),
  message: z.string(),
  errorLocation: RuleValidationErrorLocationEnumZ.nullish(),
  fields: RuleValidationFieldSchema.array().nullish(),
});

const ConditionalTermsResponseSchema = z.object({
  displayOrder: z.number(),
  editableOnQuote: z.boolean(),
  terms: z.string(),
});

const ActionRequestSchema = z.object({
  type: z.literal(RuleActionTypeEnum.APPROVAL),
  approvals: MultiApprovalsRuleResponseSchema.omit({ type: true }).array(),
});

export type ActionRequest = z.infer<typeof ActionRequestSchema>;

const ActionResponseBaseSchema = z.object({
  type: RuleActionTypeEnumZ,
  ruleId: z.string(),
  ruleName: z.string(),
  description: z.string().nullish(),
});

export const ActionResponseApprovalSchema = ActionResponseBaseSchema.extend({
  approval: ApprovalRuleResponseSchema,
  approvers: MultiApprovalsRuleResponseSchema.array(),
});
export type ActionResponseApproval = z.infer<
  typeof ActionResponseApprovalSchema
>;

export const ActionResponseValidationSchema = ActionResponseBaseSchema.extend({
  validation: ValidationRuleResponseSchema,
});
export type ActionResponseValidation = z.infer<
  typeof ActionResponseValidationSchema
>;

export const ActionResponseConditionalTermSchema =
  ActionResponseBaseSchema.extend({
    conditionalTerms: ConditionalTermsResponseSchema,
  });
export type ActionResponseConditionalTerm = z.infer<
  typeof ActionResponseConditionalTermSchema
>;

export const ActionResponseSchema = z.union([
  ActionResponseApprovalSchema,
  ActionResponseValidationSchema,
  ActionResponseConditionalTermSchema,
]);

export const ActionRecordAutomationResponseSchema =
  ActionResponseBaseSchema.extend({
    recordUpdate: RuleActionRecordAutomationValueSchema,
  });
export type ActionRecordAutomationResponse = z.infer<
  typeof ActionRecordAutomationResponseSchema
>;

/**
 * [REQUEST] validateRule
 */
export const GetFormDataRequestSchema = z.object({
  customFields: CustomFieldListResSchema.array().nullish(),
  // TODO: make this required after FE goes to production
  entity: EntityGroupEnumZ.default(EntityGroupEnum.QUOTE),
  // This only matters for RECORD_AUTOMATION rules as it includes additional response data
  // TODO: make this required after FE goes to production
  type: RuleTypeEnumZ.default(RuleTypeEnum.APPROVAL),
});
export type GetFormDataRequest = z.infer<typeof GetFormDataRequestSchema>;

/**
 * [RESPONSE] validateRule
 */
export const GetFormDataResponseSchema = z.object({
  valid: z.boolean(),
  error: z.any().nullish().optional(),
  errorMessage: z.string().nullish().optional(),
});
export type GetFormDataResponse = z.infer<typeof GetFormDataResponseSchema>;

/**
 * [REQUEST] applyRules
 */
export const ValidateExpressionRequestSchema = z.object({
  expression: z.string(),
  items: z.number().array().nullish(),
});
export type ValidateExpressionRequest = z.infer<
  typeof ValidateExpressionRequestSchema
>;

/**
 * [RESPONSE] ValidateExpression
 */
export const ValidateExpressionResponseSchema = z.object({
  success: z.boolean(),
  parseSuccess: z.boolean(),
  itemValidationSuccess: z.boolean().nullish(),
  parseResults: z.any().nullish(), // AndOrExpressionWithValues
  formattedExpression: z.string().nullish(),
  error: z.string().nullish(),
});
export type ValidateExpressionResponse = z.infer<
  typeof ValidateExpressionResponseSchema
>;

export const ApplyRulesFactsDefaultDocumentsSchema = z.object({
  defaultDocumentLocation: z.string().nullish(),
  defaultDocumentUrl: z.string().nullish(),
  defaultDocumentCover: z.string().nullish(),
  defaultDocumentFooter: z.string().nullish(),
});
export type ApplyRulesFactsDefaultDocuments = z.infer<
  typeof ApplyRulesFactsDefaultDocumentsSchema
>;

export const OfferingProductFactSchema = z.object({
  offeringId: z.string(),
  offeringStatus: OfferingStatusEnumZ,
  offeringStartDate: z.string().nullish(),
  offeringEndDate: z.string().nullish(),
  rateId: z.string(),
  rateStatus: RateStatusEnumZ,
  rateStartDate: z.string().nullish(),
  rateEndDate: z.string().nullish(),
  productId: z.string(),
  productStatus: ProductStatusEnumZ,
});
export type OfferingProductFact = z.infer<typeof OfferingProductFactSchema>;

export const ApplyRulesQuoteFactsSchema = z.object({
  quote: z.any(),
  account: z.record(z.string(), z.any()).nullish(),
  ownerTeams: z
    .object({
      teams: z.array(z.string()),
    })
    .nullish(),
  quoteDefaultDocument: ApplyRulesFactsDefaultDocumentsSchema.nullish(),
  quoteRevenueByYear: z.array(QuoteRevenueByYearSchema).nullish(),
  offeringProductFacts: z.array(OfferingProductFactSchema).nullish(),
});
export type ApplyRulesQuoteFacts = z.infer<typeof ApplyRulesQuoteFactsSchema>;
export type ApplyRulesQuoteShareFacts = ApplyRulesQuoteFacts;

/**
 * [REQUEST] applyRules
 */
export const ApplyRulesQuoteRequestSchema = z.object({
  target: EntityGroupEnumZ,
  facts: ApplyRulesQuoteFactsSchema,
});
export type ApplyRulesQuoteRequest = z.infer<
  typeof ApplyRulesQuoteRequestSchema
>;

export const ApplyRulesRequestSchema = z.discriminatedUnion('target', [
  ApplyRulesQuoteRequestSchema,
]);
export type ApplyRulesRequest = z.infer<typeof ApplyRulesRequestSchema>;

/**
 * [RESPONSE] applyRules
 */
export const ApplyRulesResponseSchema = z.object({
  status: z.enum(['SUCCESS', 'FAILED']),
  actions: ActionResponseSchema.array(),
});
export type ApplyRulesResponse = z.infer<typeof ApplyRulesResponseSchema>;

/**
 * [REQUEST] applyRecordAutomationRules
 */
export const ApplyRecordAutomationBillGroupRulesRequestFactsSchema = z.object({
  billGroup: z.any().nullish(),
  quote: z.any().nullish(),
  ownerTeams: z
    .object({
      teams: z.array(z.string()),
    })
    .nullish(),
  quoteDefaultDocument: ApplyRulesFactsDefaultDocumentsSchema.nullish(),
});
export type ApplyRecordAutomationBillGroupRulesRequestFacts = z.infer<
  typeof ApplyRecordAutomationBillGroupRulesRequestFactsSchema
>;

export const ApplyRecordAutomationBillGroupRulesRequestSchema = z.object({
  target: z.literal(EntityGroupEnum.BILL_GROUP),
  facts: ApplyRecordAutomationBillGroupRulesRequestFactsSchema,
});
export type ApplyRecordAutomationBillGroupRulesRequest = z.infer<
  typeof ApplyRecordAutomationBillGroupRulesRequestSchema
>;

export const ApplyRecordAutomationQuoteShareRulesRequestSchema = z.object({
  target: z.literal(EntityGroupEnum.QUOTE_SHARE),
  facts: ApplyRulesQuoteFactsSchema,
});
export type ApplyRecordAutomationQuoteShareRulesRequest = z.infer<
  typeof ApplyRecordAutomationQuoteShareRulesRequestSchema
>;

export const ApplyRecordAutomationInvoiceRulesRequestSchema = z.object({
  target: z.literal(EntityGroupEnum.INVOICE),
  facts: z.any(),
});
export type ApplyRecordAutomationInvoiceRulesRequest = z.infer<
  typeof ApplyRecordAutomationInvoiceRulesRequestSchema
>;

export const ApplyRecordAutomationCustomerPortalRulesRequestFactsSchema =
  z.object({
    billGroup: z.any().nullish(),
    ownerTeams: z
      .object({
        teams: z.array(z.string()),
      })
      .nullish(),
  });
export type ApplyRecordAutomationCustomerPortalRulesRequestFacts = z.infer<
  typeof ApplyRecordAutomationCustomerPortalRulesRequestFactsSchema
>;

export const ApplyRecordAutomationCustomerPortalRulesRequestSchema = z.object({
  target: z.literal(EntityGroupEnum.CUSTOMER_PORTAL),
  facts: ApplyRecordAutomationCustomerPortalRulesRequestFactsSchema,
});
export type ApplyRecordAutomationCustomerPortalRulesRequest = z.infer<
  typeof ApplyRecordAutomationCustomerPortalRulesRequestSchema
>;

export const ApplyRecordAutomationRulesRequestSchema = z.discriminatedUnion(
  'target',
  [
    ApplyRecordAutomationBillGroupRulesRequestSchema,
    ApplyRecordAutomationInvoiceRulesRequestSchema,
    ApplyRecordAutomationQuoteShareRulesRequestSchema,
    ApplyRecordAutomationCustomerPortalRulesRequestSchema,
  ],
);
export type ApplyRecordAutomationRulesRequest = z.infer<
  typeof ApplyRecordAutomationRulesRequestSchema
>;

/**
 * [RESPONSE] applyRecordAutomationRules
 */
export const ApplyRecordAutomationRulesResponseSchema = z.object({
  status: z.enum(['SUCCESS', 'FAILED']),
  actions: ActionRecordAutomationResponseSchema.array(),
});
export type ApplyRecordAutomationRulesResponse = z.infer<
  typeof ApplyRecordAutomationRulesResponseSchema
>;

/**
 * [REQUEST] testRule
 */
export const TestRuleRequestSchema = z.object({
  target: EntityGroupEnumZ,
  rule: RuleSchema,
  record: z.record(z.string(), z.any()),
  type: RuleActionTypeEnumZ,
  account: z.record(z.string(), z.any()).nullish(),
  ownerTeams: z
    .object({
      teams: z.array(z.string()),
    })
    .nullish(),
  quoteDefaultDocument: ApplyRulesFactsDefaultDocumentsSchema.nullish(),
});
export type TestRuleRequest = z.infer<typeof TestRuleRequestSchema>;

/**
 * [RESPONSE] testRule
 */
export const TestRuleResponseSchema = z.object({
  results: RuleTestEvaluationResponseSchema,
});
export type TestRuleResponse = z.infer<typeof TestRuleResponseSchema>;

/**
 * [REQUEST] testRule
 */
export const TestAllRulesRequestSchema = z.object({
  rule: RuleSchema,
  record: z.record(z.string(), z.any()),
  account: z.record(z.string(), z.any()).nullish(),
});
export type TestAllRulesRequest = z.infer<typeof TestAllRulesRequestSchema>;

/**
 * [RESPONSE] testRule
 */
export const TestAllRulesResponseSchema = z.object({
  actions: ActionRecordAutomationResponseSchema.array(),
});
export type TestAllRulesResponse = z.infer<typeof TestAllRulesResponseSchema>;

/**
 * [REQUEST] previewTerms
 * Resolve {{...}} template expressions in rich-text terms against a
 * caller-supplied record. The caller provides all data; the rules service
 * does not fetch the quote or account.
 */
export const PreviewTermsRequestSchema = z.object({
  record: z.object({
    quote: z.record(z.string(), z.any()),
    account: z.record(z.string(), z.any()).nullish(),
  }),
  terms: z.array(z.string()).max(100),
  locale: z.string().nullish(),
  /**
   * Whether list-item text should be span-wrapped, matching how the PDF
   * renders this term type: true for conditional terms, false for contract
   * terms. Defaults to true.
   */
  wrapListItems: z.boolean().nullish(),
});
export type PreviewTermsRequest = z.infer<typeof PreviewTermsRequestSchema>;

/**
 * [RESPONSE] previewTerms
 */
export const PreviewTermsResponseSchema = z.object({
  /** Resolved terms in the same order as the request `terms` array */
  resolvedTerms: z.array(z.string()),
});
export type PreviewTermsResponse = z.infer<typeof PreviewTermsResponseSchema>;

/**
 * [REQUEST] validateRule
 */
export const ValidateRuleRequestSchema = z.object({
  rule: RuleSchema,
});
export type ValidateRuleRequest = z.infer<typeof ValidateRuleRequestSchema>;

/**
 * [RESPONSE] validateRule
 */
export const ValidateRuleResponseSchema = z.object({
  valid: z.boolean(),
  error: z.any().nullish().optional(),
  message: z.string().nullish().optional(),
});
export type ValidateRuleResponse = z.infer<typeof ValidateRuleResponseSchema>;

export enum ApproverTypeEnum {
  PERSON = 'PERSON',
  TEAM = 'TEAM',
}
export const ApproverTypeEnumZ = z.enum(ApproverTypeEnum);

/** Quote (approval, validation, conditional terms) */
export const QuoteRelatedRuleTypes = [
  RuleTypeEnum.APPROVAL,
  RuleTypeEnum.VALIDATION,
  RuleTypeEnum.CONDITIONAL_TERM,
];
/** Record automation */
export const RecordAutomationRelatedRuleTypes = [
  RuleTypeEnum.RECORD_AUTOMATION,
];
/**
 * Get the group type for a rule type
 */
export const getGroupRuleType = (type: RuleTypeEnum): RuleGroupTypeEnum => {
  if (QuoteRelatedRuleTypes.includes(type)) {
    return RuleGroupTypeEnum.QUOTE;
  }
  if (RecordAutomationRelatedRuleTypes.includes(type)) {
    return RuleGroupTypeEnum.RECORD_AUTOMATION;
  }
  return RuleGroupTypeEnum.UNKNOWN;
};
export enum RuleGroupTypeEnum {
  /**
   * Approval, validation, conditional terms
   */
  QUOTE = 'QUOTE',
  /**
   * Record automation
   */
  RECORD_AUTOMATION = 'RECORD_AUTOMATION',
  UNKNOWN = 'UNKNOWN',
}

export const RecordAutomationGroupRuleTargets = {
  [EntityGroupEnum.BILL_GROUP]: 'Bill Group',
  [EntityGroupEnum.QUOTE_SHARE]: 'Quote Share',
  [EntityGroupEnum.CUSTOMER_PORTAL]: 'Customer Portal',
};

export type ApproverIdentity = {
  userId?: string | null;
  username?: string | null;
  teamId?: string | null;
  teamName?: string | null;
};
